export type NetworkEngineType = 'evm' | 'stellar';

export interface NetworkConfig {
  chainId: number;
  name: string;
  rpcUrl: string;
  blockExplorer: string;
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
  enabled: boolean;
  /** Defaults to 'evm' when omitted (legacy entries). */
  type?: NetworkEngineType;
  /** Required for Stellar / Soroban clients. */
  networkPassphrase?: string;
}

export const NETWORKS: Record<string, NetworkConfig> = {
  // Ethereum Mainnet
  ethereum: {
    chainId: 1,
    name: 'Ethereum',
    rpcUrl: 'https://mainnet.infura.io/v3/YOUR_PROJECT_ID',
    blockExplorer: 'https://etherscan.io',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    enabled: false,
    type: 'evm',
  },

  // Polygon
  polygon: {
    chainId: 137,
    name: 'Polygon',
    rpcUrl: 'https://polygon-rpc.com',
    blockExplorer: 'https://polygonscan.com',
    nativeCurrency: {
      name: 'MATIC',
      symbol: 'MATIC',
      decimals: 18,
    },
    enabled: false,
    type: 'evm',
  },

  // BSC
  bsc: {
    chainId: 56,
    name: 'BSC',
    rpcUrl: 'https://bsc-dataseed.binance.org',
    blockExplorer: 'https://bscscan.com',
    nativeCurrency: {
      name: 'BNB',
      symbol: 'BNB',
      decimals: 18,
    },
    enabled: false,
    type: 'evm',
  },

  // Arbitrum
  arbitrum: {
    chainId: 42161,
    name: 'Arbitrum One',
    rpcUrl: 'https://arb1.arbitrum.io/rpc',
    blockExplorer: 'https://arbiscan.io',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    enabled: false,
    type: 'evm',
  },

  // Optimism
  optimism: {
    chainId: 10,
    name: 'Optimism',
    rpcUrl: 'https://mainnet.optimism.io',
    blockExplorer: 'https://optimistic.etherscan.io',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    enabled: false,
    type: 'evm',
  },

  // Avalanche
  avalanche: {
    chainId: 43114,
    name: 'Avalanche',
    rpcUrl: 'https://api.avax.network/ext/bc/C/rpc',
    blockExplorer: 'https://snowtrace.io',
    nativeCurrency: {
      name: 'AVAX',
      symbol: 'AVAX',
      decimals: 18,
    },
    enabled: false,
    type: 'evm',
  },

  // Base
  base: {
    chainId: 8453,
    name: 'Base',
    rpcUrl: 'https://mainnet.base.org',
    blockExplorer: 'https://basescan.org',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    enabled: false,
    type: 'evm',
  },

  // Testnets
  sepolia: {
    chainId: 11155111,
    name: 'Sepolia',
    rpcUrl: 'https://ethereum-sepolia-rpc.publicnode.com',
    blockExplorer: 'https://sepolia.etherscan.io',
    nativeCurrency: {
      name: 'Sepolia Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    enabled: true,
    type: 'evm',
  },

  mumbai: {
    chainId: 80001,
    name: 'Mumbai',
    rpcUrl: 'https://rpc-mumbai.maticvigil.com',
    blockExplorer: 'https://mumbai.polygonscan.com',
    nativeCurrency: {
      name: 'MATIC',
      symbol: 'MATIC',
      decimals: 18,
    },
    enabled: false,
    type: 'evm',
  },

  baseSepolia: {
    chainId: 84532,
    name: 'Base Sepolia',
    rpcUrl: 'https://sepolia.base.org',
    blockExplorer: 'https://sepolia.basescan.org',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    enabled: true,
    type: 'evm',
  },

  xrplEvmSidechainMainnet: {
    chainId: 1440000,
    name: 'XRPL EVM',
    rpcUrl: 'https://rpc.xrplevm.org',
    blockExplorer: 'https://explorer.xrplevm.org',
    nativeCurrency: {
      name: 'XRP',
      symbol: 'XRP',
      decimals: 18,
    },
    enabled: true,
    type: 'evm',
  },

  xrplEvmSidechainTestnet: {
    chainId: 1449000,
    name: 'XRPL EVM Testnet',
    rpcUrl: 'https://rpc.testnet.xrplevm.org',
    blockExplorer: 'https://explorer.testnet.xrplevm.org',
    nativeCurrency: {
      name: 'XRP',
      symbol: 'XRP',
      decimals: 18,
    },
    enabled: true,
    type: 'evm',
  },

  hyperliquid: {
    chainId: 999,
    name: 'Hyperliquid',
    rpcUrl: 'https://rpc.hyperliquid.xyz/evm',
    blockExplorer: 'https://hyperevmscan.io',
    nativeCurrency: {
      name: 'HYPE',
      symbol: 'HYPE',
      decimals: 18,
    },
    enabled: true,
    type: 'evm',
  },

  hyperliquidTestnet: {
    chainId: 998,
    name: 'Hyperliquid Testnet',
    rpcUrl: 'https://rpc.hyperliquid-testnet.xyz/evm',
    blockExplorer: 'https://app.hyperliquid-testnet.xyz/explorer',
    nativeCurrency: {
      name: 'HYPE',
      symbol: 'HYPE',
      decimals: 18,
    },
    enabled: true,
    type: 'evm',
  },

  // Stellar / Soroban (TrustLine chain IDs: 1 mainnet, 2 testnet, 3 futurenet)
  stellarMainnet: {
    chainId: 1,
    name: 'Stellar Mainnet',
    rpcUrl: 'https://soroban-rpc.mainnet.stellar.gateway.fm',
    blockExplorer: 'https://stellarchain.io',
    nativeCurrency: {
      name: 'Lumen',
      symbol: 'XLM',
      decimals: 7,
    },
    enabled: true,
    type: 'stellar',
    networkPassphrase: 'Public Global Stellar Network ; September 2015',
  },

  stellarTestnet: {
    chainId: 2,
    name: 'Stellar Testnet',
    rpcUrl: 'https://soroban-testnet.stellar.org',
    blockExplorer: 'https://stellar.expert/explorer/testnet',
    nativeCurrency: {
      name: 'Lumen',
      symbol: 'XLM',
      decimals: 7,
    },
    enabled: true,
    type: 'stellar',
    networkPassphrase: 'Test SDF Network ; September 2015',
  },

  stellarFuturenet: {
    chainId: 3,
    name: 'Stellar Futurenet',
    rpcUrl: 'https://rpc-futurenet.stellar.org',
    blockExplorer: 'https://stellar.expert/explorer/futurenet',
    nativeCurrency: {
      name: 'Lumen',
      symbol: 'XLM',
      decimals: 7,
    },
    enabled: true,
    type: 'stellar',
    networkPassphrase: 'Test SDF Future Network ; October 2022',
  },
};

export const getNetworkEngineType = (network: NetworkConfig | null | undefined): NetworkEngineType => {
  return network?.type || 'evm';
};

export const isStellarNetwork = (network: NetworkConfig | null | undefined): boolean => {
  return getNetworkEngineType(network) === 'stellar';
};

export const getNetworkByChainId = (
  chainId: number,
  engine?: NetworkEngineType
): NetworkConfig | undefined => {
  const matches = Object.values(NETWORKS).filter(network => network.chainId === chainId);
  if (engine) {
    return matches.find(network => getNetworkEngineType(network) === engine);
  }
  // Prefer EVM on ambiguous chain IDs (wallet EIP-1193 detection vs Stellar TrustLine IDs)
  return (
    matches.find(network => getNetworkEngineType(network) === 'evm') ||
    matches.find(network => network.enabled) ||
    matches[0]
  );
};

/**
 * Network for a stored dApp config. The engine type disambiguates chain IDs that
 * TrustLine reuses across engines (1 is Ethereum for EVM, Stellar Mainnet for Stellar).
 * Returns undefined for engines without a network table here (e.g. Solana).
 */
export const getNetworkForDApp = (
  chainId: string | number,
  type?: string | null
): NetworkConfig | undefined => {
  const numericChain = Number(chainId);
  const engine = (type || '').trim().toLowerCase();
  if (Number.isNaN(numericChain) || (engine !== 'evm' && engine !== 'stellar')) {
    return undefined;
  }
  return getNetworkByChainId(numericChain, engine);
};

export const getSupportedNetworks = (): NetworkConfig[] => {
  return Object.values(NETWORKS).filter(network => network.enabled);
};

export const DEFAULT_NETWORK = 'ethereum';
